import { useState, useCallback } from "react";
import { categoriesApi, type BlogCategory } from "@/integrations/mysql/client";
import { useToast } from "@/hooks/use-toast";

export type { BlogCategory };

// Mantém compatibilidade com CategoryFormData do componente CategoryForm
export interface CategoryFormData {
  name: string;
  color_hex?: string;
  border_color_hex?: string;
  text_color_hex?: string;
}

export function useCategoryOperations() {
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const { toast }                   = useToast();

  const fetchCategories = useCallback(async () => {
    try {
      const data = await categoriesApi.list();
      setCategories(data);
    } catch (err: unknown) {
      toast({ title: "Erro", description: "Erro ao carregar categorias.", variant: "destructive" });
    }
  }, [toast]);

  const createCategory = useCallback(async (formData: CategoryFormData) => {
    try {
      await categoriesApi.create({
        name: formData.name,
        color_hex: formData.color_hex ?? '#1F5938',
        border_color_hex: formData.border_color_hex ?? '#1F5938',
        text_color_hex: formData.text_color_hex ?? '#FFFFFF',
        created_at: '',
        updated_at: '',
      });
      toast({ title: "Sucesso!", description: "Categoria criada com sucesso." });
      await fetchCategories();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro ao criar categoria.";
      toast({ title: "Erro", description: msg, variant: "destructive" });
      throw err;
    }
  }, [toast, fetchCategories]);

  const updateCategory = useCallback(async (categoryId: string, formData: CategoryFormData) => {
    try {
      await categoriesApi.update(categoryId, formData);
      toast({ title: "Sucesso!", description: "Categoria atualizada com sucesso." });
      await fetchCategories();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro ao atualizar categoria.";
      toast({ title: "Erro", description: msg, variant: "destructive" });
      throw err;
    }
  }, [toast, fetchCategories]);

  const deleteCategory = useCallback(async (categoryId: string) => {
    try {
      await categoriesApi.delete(categoryId);
      toast({ title: "Sucesso!", description: "Categoria excluída com sucesso." });
      await fetchCategories();
    } catch (err: unknown) {
      toast({ title: "Erro", description: "Erro ao excluir categoria.", variant: "destructive" });
    }
  }, [toast, fetchCategories]);

  return { categories, fetchCategories, createCategory, updateCategory, deleteCategory };
}
